/**
 * O campo de CBO do cargo: busca na tabela oficial (CBO 2002) por código, título ou nome
 * popular — "pedreiro", "auxiliar de limpeza" — e preenche o código no formato XXXX-XX. Digitar o código
 * direto continua valendo, como antes.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import cboService, { type OcupacaoCbo } from '../../../services/cboService';

export interface CampoCboProps {
  /** O código no formato do cargo (`4110-05`) ou vazio. */
  valor: string;
  onChange: (codigo: string) => void;
  /** Chamado ao escolher uma ocupação da lista — quem usa decide se aproveita o título. */
  onEscolher?: (ocupacao: OcupacaoCbo) => void;
  erro?: string;
  disabled?: boolean;
}

/** Mesmo comportamento da máscara que o campo já tinha. */
function mascara(valor: string): string {
  const numeros = valor.replace(/\D/g, '').slice(0, 6);
  return numeros.length <= 4 ? numeros : `${numeros.slice(0, 4)}-${numeros.slice(4)}`;
}

export const CampoCbo: React.FC<CampoCboProps> = ({ valor, onChange, onEscolher, erro, disabled = false }) => {
  const [texto, setTexto] = useState(valor);
  const [opcoes, setOpcoes] = useState<OcupacaoCbo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const ultimaBusca = useRef('');

  // O valor muda por fora quando o cargo em edição termina de carregar. Ajuste durante o render,
  // como o React recomenda para estado derivado de prop — sem efeito e sem render em cascata.
  const [valorAnterior, setValorAnterior] = useState(valor);
  if (valor !== valorAnterior) {
    setValorAnterior(valor);
    if (texto === '' || texto === mascara(texto)) setTexto(valor);
  }

  useEffect(() => {
    const termo = texto.trim();
    if (termo.length < 2 || termo === ultimaBusca.current) return;
    let vivo = true;
    const espera = window.setTimeout(() => {
      ultimaBusca.current = termo;
      setCarregando(true);
      cboService
        .buscar(termo)
        .then((achadas) => {
          if (vivo) setOpcoes(achadas);
        })
        .catch(() => {
          if (vivo) setOpcoes([]);
        })
        .finally(() => {
          if (vivo) setCarregando(false);
        });
    }, 300);
    return () => {
      vivo = false;
      window.clearTimeout(espera);
    };
  }, [texto]);

  return (
    <Autocomplete<OcupacaoCbo, false, false, true>
      freeSolo
      options={opcoes}
      filterOptions={(lista) => lista}
      inputValue={texto}
      onInputChange={(_, novo, motivo) => {
        // Só números (e hífen) é código digitado à mão: aplica a máscara e já vale como valor.
        if (motivo === 'input' && /^[\d\s-]*$/.test(novo)) {
          const comMascara = mascara(novo);
          setTexto(comMascara);
          onChange(comMascara);
          return;
        }
        setTexto(novo);
      }}
      onChange={(_, escolhida) => {
        if (escolhida && typeof escolhida !== 'string') {
          setTexto(escolhida.codigoFormatado);
          onChange(escolhida.codigoFormatado);
          onEscolher?.(escolhida);
        }
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.codigoFormatado)}
      loading={carregando}
      disabled={disabled}
      noOptionsText="Nenhuma ocupação com esse nome ou código na CBO"
      renderOption={(props, o) => (
        <li {...props} key={o.codigo}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">
              <strong>{o.codigoFormatado}</strong> — {o.titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {o.sinonimo ? `Também chamado de "${o.sinonimo}" · ` : ''}
              {o.familia}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="CBO (Código Brasileiro de Ocupações)"
          placeholder="Digite o código ou o nome da ocupação"
          error={Boolean(erro)}
          helperText={erro ?? 'Busque por código, nome ou nome popular (ex.: pedreiro, auxiliar de limpeza)'}
          fullWidth
        />
      )}
    />
  );
};

export default CampoCbo;
